<script setup lang="ts">
/**
 * Image with a loading state (FE-005).
 *
 * Until the file has decoded, a `media` skeleton holds the box and the image is
 * transparent; then it fades in (no snap). Cached images report `complete` on
 * mount, so they never flash a skeleton. A failed load leaves the skeleton's
 * base tone instead of a hole, with the shimmer stopped.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'

interface FairImageProps {
  src?: string
  alt?: string
  /** `cover` (default) / `contain` — applied to the <img>. */
  fit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  loading?: 'lazy' | 'eager'
  fetchpriority?: 'high' | 'low' | 'auto'
  decoding?: 'sync' | 'async' | 'auto'
  sizes?: string
  srcset?: string
  radius?: string
  /** Draw the FR mark in the skeleton (default: yes, as for any media box). */
  mark?: boolean
  imgClass?: string
}

const props = withDefaults(defineProps<FairImageProps>(), {
  src: undefined,
  alt: '',
  fit: 'cover',
  loading: 'lazy',
  fetchpriority: undefined,
  decoding: 'async',
  sizes: undefined,
  srcset: undefined,
  radius: undefined,
  mark: undefined,
  imgClass: undefined,
})

const emit = defineEmits<{
  load: [event: Event]
  error: [event: Event]
}>()

const imgRef = ref<HTMLImageElement | null>(null)
const loaded = ref(false)
const failed = ref(false)

const rootClass = computed(() => [
  'fair-image',
  {
    'fair-image--loading': !loaded.value && !failed.value,
    'fair-image--failed': failed.value,
  },
])

const rootStyle = computed(() => ({ borderRadius: props.radius }))

const imgStyle = computed(() => ({ objectFit: props.fit }))

function onLoad(event: Event) {
  loaded.value = true
  failed.value = false
  emit('load', event)
}

function onError(event: Event) {
  failed.value = true
  emit('error', event)
}

/** Cached images fire no `load` after hydration — read `complete` instead. */
function syncFromDom() {
  const el = imgRef.value
  if (!el) return
  if (el.complete && el.naturalWidth > 0) loaded.value = true
}

onMounted(syncFromDom)

watch(
  () => props.src,
  () => {
    loaded.value = false
    failed.value = false
    void Promise.resolve().then(syncFromDom)
  },
)
</script>

<template>
  <span
    :class="rootClass"
    :style="rootStyle"
  >
    <FairSkeleton
      v-if="!loaded"
      class="fair-image__placeholder"
      variant="media"
      fill
      :mark="mark"
      :radius="radius"
    />
    <img
      ref="imgRef"
      class="fair-image__img"
      :class="imgClass"
      :style="imgStyle"
      :src="src"
      :alt="alt"
      :loading="loading"
      :fetchpriority="fetchpriority"
      :decoding="decoding"
      :sizes="sizes"
      :srcset="srcset"
      @load="onLoad"
      @error="onError"
    >
  </span>
</template>
